import React from 'react';
import PropTypes from 'prop-types';
import { ActionIcon, Button, Divider, Tooltip } from '@mantine/core';
import { Link } from 'react-router-dom';
import {
  ArrowBackUp,
  ArrowForwardUp,
  BucketOff,
  Edit,
  InfoCircle,
  Logout,
  Tools
} from 'tabler-icons-react';
import AppStack from '../../common/AppStack';
import AppFlexbox from '../../common/AppFlexbox';
import AppCard from '../../common/AppCard';
import AppText from '../../common/AppText';
import { useMediaQueryIndex } from '../../../helpers/hooks';
import { singularPluralFormat } from '../../../helpers/format';

const PrintDesignEditorHeader = ({
  ecomExternalProducts,
  formState,
  setFormState,
  canvasController,
  setPrintTemplateState,
  printTemplateState,
  setToolbarMenuState,
  hasChanges
}) => {
  const { isTabletOrSmaller } = useMediaQueryIndex();
  const { changeBackgroundColor, undo, redo, getExport } = canvasController;

  return (
    <AppFlexbox style={{ width: '100%', height: 60 }}>
      <AppCard
        shadow="md"
        style={{
          padding: 0,
          width: '100%',
          height: '100%'
        }}
        withBorder
      >
        <AppFlexbox
          style={{ height: '100%', gap: 0, flex: 1, alignItems: 'center' }}
        >
          <AppFlexbox
            component={Link}
            style={{
              alignItems: 'center',
              gap: 5,
              cursor: 'pointer',
              textDecoration: 'none',
              color: '#000',
              padding: isTabletOrSmaller ? 10 : '10px 20px'
            }}
            to="/vendor/products"
          >
            <Logout size={20} style={{ transform: 'rotate(180deg)' }} />
            <AppText>Exit</AppText>
          </AppFlexbox>
          <Divider orientation="vertical" />

          <AppFlexbox
            style={{
              flex: 1,
              gap: 5,
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <AppStack
              style={{
                gap: 0,
                padding: isTabletOrSmaller ? 10 : '10px 20px'
              }}
            >
              <AppText
                style={{ fontWeight: 500, fontSize: 16, lineHeight: '24px' }}
              >
                Design{' '}
                {singularPluralFormat(
                  ecomExternalProducts.length,
                  'product',
                  'products'
                )}
              </AppText>
              <AppText
                style={{
                  fontSize: 14,
                  color: '#666',
                  lineHeight: '16px',
                  whiteSpace: 'nowrap'
                }}
                visibleFrom="sm"
              >
                Customize the print design for your products.
              </AppText>
            </AppStack>
          </AppFlexbox>

          <AppFlexbox style={{ padding: isTabletOrSmaller ? 10 : '10px 20px' }}>
            {formState.printPreview?.showPreview ? (
              <Button
                color="blue"
                leftSection={<Edit size={18} />}
                onClick={() => {
                  setFormState((c) => ({
                    ...c,
                    printPreview: {
                      previewImage: null,
                      previewFile: null,
                      showPreview: false
                    }
                  }));
                }}
                size="compact-md"
                variant="outline"
              >
                Edit view
              </Button>
            ) : (
              <></>
              // <Button
              //   color="dark"
              //   leftSection={<Eye size={18} />}
              //   onClick={async () => {
              //     const previewImage = await getExport();

              //     dataUrlToFile(previewImage, 'image', (file) => {
              //       setFormState((c) => ({
              //         ...c,
              //         printPreview: {
              //           previewImage,
              //           previewFile: file,
              //           showPreview: true
              //         }
              //       }));
              //     });
              //   }}
              //   size="compact-md"
              //   variant="filled"
              // >
              //   Preview print
              // </Button>
            )}
          </AppFlexbox>

          <AppFlexbox style={{ padding: 8 }}>
            <AppStack>
              <ActionIcon
                color="#666"
                onClick={() => {
                  setFormState((c) => ({
                    ...c,
                    showCanvasInfoCard: !c.showCanvasInfoCard,
                    showVariantSelect: false
                  }));
                  setToolbarMenuState((c) => ({
                    ...c,
                    isOpen: false
                  }));
                }}
                size={32}
                variant="subtle"
              >
                <InfoCircle size={32} />
              </ActionIcon>
            </AppStack>

            <Tooltip label="Hide variant color" withArrow>
              <Button
                color="dark"
                onClick={() => {
                  setFormState((c) => ({
                    ...c,
                    disableTemplateBackgroundColor: !c.disableTemplateBackgroundColor
                  }));
                  if (!formState.disableTemplateBackgroundColor) {
                    changeBackgroundColor('#FFF');
                  }
                  else {
                    changeBackgroundColor(
                      formState.selectedColor?.primaryColor || '#FFF'
                    );
                  }
                }}
                size="compact-md"
                variant={
                  formState.disableTemplateBackgroundColor
                    ? 'filled'
                    : 'outline'
                }
              >
                <BucketOff size={18} />
              </Button>
            </Tooltip>

            <Tooltip label="Variants" withArrow>
              <Button
                color="dark"
                onClick={() => {
                  setFormState((c) => ({
                    ...c,
                    showVariantSelect: !c.showVariantSelect,
                    showCanvasInfoCard: false
                  }));
                  setToolbarMenuState((c) => ({
                    ...c,
                    isOpen: false
                  }));
                }}
                size="compact-md"
                variant={formState.showVariantSelect ? 'filled' : 'outline'}
              >
                <Tools size={18} />
              </Button>
            </Tooltip>
          </AppFlexbox>

          <Divider orientation="vertical" />

          <AppFlexbox style={{ padding: isTabletOrSmaller ? 10 : '10px 20px' }}>
            <Button.Group>
              <Tooltip label="Undo" withArrow>
                <Button
                  color="dark"
                  disabled={printTemplateState.currentHistoryIndex <= 0}
                  onClick={undo}
                  size="compact-md"
                  variant="filled"
                >
                  <ArrowBackUp size={18} />
                </Button>
              </Tooltip>

              <Tooltip label="Redo" withArrow>
                <Button
                  color="dark"
                  disabled={
                    printTemplateState.currentHistoryIndex >=
                    printTemplateState.history.length - 1
                  }
                  onClick={redo}
                  size="compact-md"
                  variant="filled"
                >
                  <ArrowForwardUp size={18} />
                </Button>
              </Tooltip>
            </Button.Group>
          </AppFlexbox>

          <Divider orientation="vertical" />

          <AppFlexbox style={{ padding: isTabletOrSmaller ? 10 : '10px 20px' }}>
            <Button
              color="blue"
              disabled={!hasChanges}
              onClick={() => {
                setFormState({ ...formState, showConfirmModal: true });
              }}
              variant="filled"
            >
              Save changes
            </Button>
          </AppFlexbox>
        </AppFlexbox>
      </AppCard>
    </AppFlexbox>
  );
};

PrintDesignEditorHeader.propTypes = {
  canvasController: PropTypes.object.isRequired,
  ecomExternalProducts: PropTypes.array.isRequired,
  formState: PropTypes.object.isRequired,
  printTemplateState: PropTypes.object.isRequired,
  setFormState: PropTypes.func.isRequired,
  setPrintTemplateState: PropTypes.func.isRequired,
  setToolbarMenuState: PropTypes.func.isRequired,
  hasChanges: PropTypes.bool
};

export default PrintDesignEditorHeader;
